/**
 * Collection & search pages: "Load more" instead of page numbers, then endless scroll.
 * - The next page is fetched in the background before the shopper needs it,
 *   so clicking "Load more" (or reaching the end) shows products straight away.
 * - After the first click the button is gone; next pages load by themselves on scroll.
 * - Changing a filter or the sort order starts again with the button.
 * Works on top of the existing <s-main-collection> element and the Searchanise helper,
 * without changing the address bar or the helper's own requests.
 * - Product images start loading about two screens ahead, and the next page's images
 *   are fetched in the background so they are ready when the cards appear.
 */
(function () {
  const isArabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
  const LABEL = isArabic ? 'تحميل المزيد' : 'Load more';
  const LOADING_LABEL = isArabic ? 'جارٍ التحميل…' : 'Loading…';

  function injectStyles() {
    if (document.getElementById('collection-load-more-styles')) return;
    const style = document.createElement('style');
    style.id = 'collection-load-more-styles';
    style.textContent = `
      .s-main-collection__load-more {
        display: flex;
        justify-content: center;
        width: 100%;
        padding-block: 48px 24px;
      }
      .s-main-collection__load-more-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        min-height: 60px;
        padding: 0 24px;
        background: #fff;
        border: 1px solid #111;
        color: #111;
        font: inherit;
        font-size: 14px;
        font-weight: 500;
        line-height: 20px;
        letter-spacing: 0.1px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
      }
      .s-main-collection__load-more-btn:hover {
        background: #111;
        color: #fff;
      }
      .s-main-collection__load-more-btn[disabled] {
        cursor: progress;
        background: #fff;
        color: #6b6b6b;
        border-color: #e1e1e1;
      }
      .s-main-collection__load-more-btn:focus-visible {
        outline: 2px solid #111;
        outline-offset: 2px;
      }
      .s-main-collection__load-sentinel {
        display: flex;
        justify-content: center;
        width: 100%;
        min-height: 1px;
        padding-block: 24px;
      }
      .s-main-collection__load-spinner {
        width: 24px;
        height: 24px;
        border: 2px solid #e1e1e1;
        border-top-color: #111;
        border-radius: 50%;
        opacity: 0;
        animation: s-main-collection-spin 0.8s linear infinite;
        transition: opacity 0.2s ease;
      }
      .s-main-collection__load-sentinel.is-waiting .s-main-collection__load-spinner {
        opacity: 1;
      }
      @keyframes s-main-collection-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  }

  // ---- Images: start loading earlier and warm the browser cache before cards are shown ----
  const EXPAND = () => Math.round(Math.max(window.innerHeight, 600) * 2); // ~2 screens ahead

  function boostLazyImages(root) {
    root.querySelectorAll('img.lazyload:not([data-expand])').forEach((img) => {
      img.setAttribute('data-expand', String(EXPAND()));
    });
  }

  function pickFromSrcset(srcset, targetWidth) {
    const candidates = String(srcset || '')
      .split(',')
      .map((part) => part.trim().split(/\s+/))
      .filter((bits) => bits[0] && /^\d+w$/.test(bits[1] || ''))
      .map((bits) => ({ url: bits[0], width: parseInt(bits[1], 10) }))
      .sort((a, b) => a.width - b.width);
    if (!candidates.length) return null;
    const match = candidates.find((candidate) => candidate.width >= targetWidth);
    return (match || candidates[candidates.length - 1]).url;
  }

  // Which image the shopper actually sees on a card right now (grid view, desktop / mobile)
  function visibleImageInfo(el) {
    const images = el.querySelectorAll('.js-main-collection__product-cards-grid img.c-product-card-custom__image');
    for (const img of images) {
      const box = img.getBoundingClientRect();
      if (box.width > 0 && box.height > 0) {
        const inSlider = !!img.closest('.c-product-card-custom__slider');
        return {
          width: Math.round(box.width * Math.min(window.devicePixelRatio || 1, 2)),
          selector: inSlider ? '.c-product-card-custom__slider img.c-product-card-custom__image' : '.c-product-card-custom__product-media img.c-product-card-custom__image',
        };
      }
    }
    return null;
  }

  function warmImages(el, html) {
    const info = visibleImageInfo(el);
    if (!info || !html) return;
    const template = document.createElement('template');
    template.innerHTML = html;
    // only the first rows: the rest start loading two screens ahead once the cards are in
    Array.from(template.content.querySelectorAll('c-product-card-custom')).slice(0, 8).forEach((card) => {
      const img = card.querySelector(info.selector);
      if (!img) return;
      const url = pickFromSrcset(img.getAttribute('data-srcset'), info.width);
      if (!url) return;
      const preload = new Image();
      preload.decoding = 'async';
      if ('fetchPriority' in preload) preload.fetchPriority = 'low';
      preload.src = url;
    });
  }

  const idle = (fn) => (window.requestIdleCallback ? requestIdleCallback(fn, { timeout: 1500 }) : setTimeout(fn, 300));

  function enhance(el) {
    if (el.__loadMoreReady) return;
    const helper = el.searchaniseHelper || (window.apps && window.apps.searchanise && window.apps.searchanise.helper);
    if (!helper || typeof el.renderCollection !== 'function' || typeof helper.buildRequestUrl !== 'function') return;
    el.__loadMoreReady = true;

    let shownPage = 1; // last page whose products are on screen
    let autoLoad = false; // turned on by the first click
    let waiting = false; // shopper asked for products that are still on their way
    let generation = 0; // bumps on every fresh list (filter / sort), so old requests are ignored
    let observer = null;
    const prefetched = new Map(); // page number -> Promise<string> (cards HTML)
    const cardCache = new Map(); // product link -> Promise<string>

    const originalRenderCollection = el.renderCollection;
    const originalRenderProductCount = el.renderProductCount;

    const container = () => el.querySelector(el.paginationContainerSelector || '.js-main-collection__pagination-container');
    const grid = () => el.querySelector(el.renderProductsContainerSelector || '.js-main-collection__product-cards-grid');
    const totalPages = () => Math.ceil(Number(helper.totalPages) || 0);
    const hasMore = () => shownPage < totalPages();

    function cardHTML(product) {
      const key = product.link || product.product_id;
      if (!cardCache.has(key)) {
        cardCache.set(key, el.getProductCardHTML(product).catch(() => ''));
      }
      return cardCache.get(key);
    }

    // Searchanise results for one page, built with the helper's own filters and sort,
    // without touching the helper's current page or the address bar.
    function requestUrlFor(page) {
      const current = helper._activePage;
      helper._activePage = page;
      const url = helper.buildRequestUrl();
      helper._activePage = current;
      return url;
    }

    function fetchPage(page) {
      if (prefetched.has(page)) return prefetched.get(page);
      const gen = generation;
      const promise = fetch(requestUrlFor(page))
        .then((response) => response.json())
        .then((data) => {
          if (gen !== generation) return '';
          const items = (data && data.items) || [];
          return Promise.all(items.map(cardHTML)).then((cards) => {
            const html = cards.join('');
            if (gen === generation) warmImages(el, html);
            return html;
          });
        })
        .catch(() => {
          prefetched.delete(page);
          return null;
        });
      prefetched.set(page, promise);
      return promise;
    }

    function prefetchNext() {
      if (!hasMore()) return;
      const next = shownPage + 1;
      idle(() => fetchPage(next));
    }

    function stopObserving() {
      if (observer) {
        observer.disconnect();
        observer = null;
      }
    }

    function showNext() {
      if (waiting || !hasMore()) return;
      const gen = generation;
      const page = shownPage + 1;
      waiting = true;
      updateWaitingState();

      fetchPage(page).then((html) => {
        if (gen !== generation) return;
        waiting = false;
        if (html === null) {
          // network error: let the shopper try again
          render();
          return;
        }
        const target = grid();
        if (target && html) target.insertAdjacentHTML('beforeend', html);
        prefetched.delete(page);
        shownPage = page;

        if (typeof el.updateVariables === 'function') el.updateVariables();
        if (typeof el.syncBigCardHeights === 'function') el.syncBigCardHeights();

        // keep "x of y products" right without moving the helper or the address bar
        helper._activePage = shownPage;
        if (typeof originalRenderProductCount === 'function') originalRenderProductCount.apply(el);

        render();
        prefetchNext();
      });
    }

    function updateWaitingState() {
      const target = container();
      if (!target) return;
      const button = target.querySelector('.js-collection-load-more');
      if (button) {
        button.disabled = waiting;
        button.textContent = waiting ? LOADING_LABEL : LABEL;
      }
      const sentinel = target.querySelector('.s-main-collection__load-sentinel');
      if (sentinel) sentinel.classList.toggle('is-waiting', waiting);
    }

    // Button before the first click, invisible sentinel (with spinner while waiting) after it
    function render() {
      const target = container();
      if (!target) return;
      stopObserving();

      if (!hasMore()) {
        target.innerHTML = '';
        return;
      }

      if (!autoLoad) {
        target.innerHTML = `
          <div class="s-main-collection__load-more">
            <button type="button" class="s-main-collection__load-more-btn js-collection-load-more">${LABEL}</button>
          </div>
        `;
        updateWaitingState();
        return;
      }

      target.innerHTML = '<div class="s-main-collection__load-sentinel" aria-hidden="true"><span class="s-main-collection__load-spinner"></span></div>';
      updateWaitingState();
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) showNext();
        },
        { root: null, rootMargin: '0px 0px 1200px 0px', threshold: 0 }
      );
      observer.observe(target.firstElementChild);
    }

    // Any list coming from the theme (first load, filter, sort) is a fresh start
    el.renderCollection = function () {
      generation += 1;
      shownPage = Number(helper.activePage) || 1;
      autoLoad = false;
      waiting = false;
      prefetched.clear();
      stopObserving();
      const result = originalRenderCollection.apply(el, arguments);
      Promise.resolve(result).then(prefetchNext);
      return result;
    };

    el.renderPagination = function () {
      render();
    };

    el.addEventListener('click', (event) => {
      const button = event.target.closest('.js-collection-load-more');
      if (!button) return;
      event.preventDefault();
      autoLoad = true;
      render(); // button is replaced by the scroll sentinel straight away
      showNext();
    });

    // Cards the theme renders itself (first load, filter, sort) also start loading earlier
    new MutationObserver(() => boostLazyImages(el)).observe(el, { childList: true, subtree: true });
    boostLazyImages(el);

    // Already rendered before this script ran
    if (totalPages() > 0) {
      shownPage = Number(helper.activePage) || 1;
      render();
      prefetchNext();
    }
  }

  function init() {
    injectStyles();
    document.querySelectorAll('s-main-collection').forEach(enhance);
  }

  if (window.customElements) {
    customElements.whenDefined('s-main-collection').then(init);
  }
})();
